/// <reference types="vite/client" />

import type { ORPCClient, Outputs } from '@/lib/orpc'
import type { QueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Devtools } from '@/components/devtools'
import { Toaster } from '@/components/ui/sonner'
import { ThemeProvider } from '@/components/ui/theme'
import { orpcMiddleware } from '@/lib/orpc'
import { seo } from '@/utils/seo'
import * as Sentry from '@sentry/tanstackstart-react'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'

import appCss from '../styles.css?url'

export const getUserFn = createServerFn({ method: 'GET' })
  .middleware([orpcMiddleware])
  .handler(async ({ context }) => {
    try {
      return await context.orpc.backoffice.auth.getCurrentUser()
    } catch (e) {
      Sentry.captureException(e)
      return null
    }
  })

export const Route = createRootRouteWithContext<{
  orpc: ORPCClient
  queryClient: QueryClient
  user?: Outputs['backoffice']['auth']['getCurrentUser']
  breadcrumb?: string
}>()({
  head: () => ({
    links: [
      { href: appCss, rel: 'stylesheet' },
      { rel: 'icon', href: '/favicon.ico' },
      {
        rel: 'apple-touch-icon',
        href: '/apple-touch-icon.png',
        sizes: '180x180'
      },
      { rel: 'manifest', href: '/site.webmanifest' }
    ],
    meta: [
      { charSet: 'utf-8' },
      {
        content: 'width=device-width, initial-scale=1',
        name: 'viewport'
      },
      ...seo({
        title: 'Back-office PesonaMalang.id',
        description:
          'Manajemen konten Back-office PesonaMalang.id | Fakultas Ilmu Komunikasi Universitas Merdeka Malang',
        keywords:
          'backoffice, desa wisata, universitas merdeka malang, fakultas ilmu komunikasi, manajemen konten, wisata desa, platform backoffice, sistem informasi desa wisata, aplikasi backoffice, pengelolaan desa wisata, teknologi informasi desa wisata, solusi backoffice, digitalisasi desa wisata',
        image: 'https://backoffice.pesonamalang.id/icon-512.png'
      }),
      { content: 'yes', name: 'mobile-web-app-capable' },
      { content: 'translucent', name: 'apple-mobile-web-app-status-bar-style' }
    ]
  }),
  component: RootComponent,
  beforeLoad: async () => {
    return {
      user: await getUserFn()
    }
  }
})

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <ThemeProvider>
      <html lang="id-ID">
        <head>
          <HeadContent />
        </head>
        <body>
          {children}
          <Toaster />
          <Devtools />
          <Scripts />
        </body>
      </html>
    </ThemeProvider>
  )
}
